import React, { Component } from 'react';
import { Form, Field } from 'react-final-form';
import { getLoadingSelector } from '../utils/asyncHelpers';
import { Row, Col, Button, Spin, notification } from 'antd';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormActions } from '../components';
import { goToClassifiers } from '../utils/gotoLink';
import classifiersStore from '../utils/classifiersStore';
import FormStateToRedux from './FormStateToRedux';

/**
 * Factory for simple classifier/parameter editor forms.
 *
 * @param {object} config
 * @param {string} config.title - translation key for the page title
 * @param {string} config.formKey - form key constant for FormStateToRedux
 * @param {Array}  config.fields - field descriptors:
 *   { name, label, component, colSpan (default 24), ...extraProps }
 * @param {Function} config.fetchAction - action creator to fetch data
 * @param {Function} config.changeAction - action creator to save data
 * @param {Function} config.getChangeSelector - selector for change state
 * @param {Function} config.getItemSelector - selector for item state
 * @param {Function} config.getInitialValues - (state) => initialValues object
 */
const createParameterEditor = (config) => {
  const {
    title,
    formKey,
    fields,
    fetchAction,
    changeAction,
    getChangeSelector,
    getItemSelector,
    getInitialValues,
  } = config;

  class ParameterEditor extends Component {
    static propTypes = {
      t: PropTypes.func.isRequired,
      fetchData: PropTypes.func.isRequired,
      changeData: PropTypes.func.isRequired,
      isLoading: PropTypes.bool.isRequired,
      initialValues: PropTypes.object,
    };

    componentDidMount = () => {
      this.props.fetchData();
    };

    onSubmit = async (values) => {
      const { t, changeData } = this.props;

      await changeData(values);

      notification.info({
        message: t('completeTitle'),
        description: t('completeBody'),
      });

      await classifiersStore.update();
    };

    render() {
      const { t, isLoading, initialValues } = this.props;

      return (
        <Spin spinning={isLoading}>
          <h1>{t(title)}</h1>
          <Form
            onSubmit={this.onSubmit}
            initialValues={initialValues}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <FormStateToRedux form={formKey} />
                <Row gutter={32}>
                  {fields.map(
                    ({ name, label, component, colSpan = 24, ...extra }) => (
                      <Col span={colSpan} key={name}>
                        <Field
                          name={name}
                          label={t(label)}
                          component={component}
                          {...extra}
                        />
                      </Col>
                    )
                  )}
                </Row>
                <FormActions>
                  <Button onClick={goToClassifiers}>{t('buttonBack')}</Button>
                  <Button htmlType="submit" type="primary">
                    {t('buttonSave')}
                  </Button>
                </FormActions>
              </form>
            )}
          />
        </Spin>
      );
    }
  }

  const getIsLoading = getLoadingSelector(getChangeSelector, getItemSelector);

  const mapStateToProps = (state) => ({
    initialValues: getInitialValues(state),
    isLoading: getIsLoading(state),
  });

  const mapDispatchToProps = {
    fetchData: fetchAction,
    changeData: changeAction,
  };

  return connect(mapStateToProps, mapDispatchToProps)(ParameterEditor);
};

export default createParameterEditor;
